import React, { useCallback } from "react";

import Checkbox from "antd/es/checkbox";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { reportsWeeklyCardAnalytics } from "@app/scene/reports/reportsAnalytics";
import WeeklyReportSetting from "@app/types/report/WeeklyReportCardSettings";

import weeklyReportsOptions from "../../utils/weeklyReportSettingOptions.json";
import Section from "../Section";

import styles from "./QuickFigures.module.scss";

export const MAX_METRICS_ALLOWED: number = 6;

interface Props {
  formData: WeeklyReportSetting["value"];
  onChange?: (formData: WeeklyReportSetting["value"]) => void;
}

const QuickFigures: React.FC<Props> = ({ formData, onChange }) => {
  const intl = useIntl();

  const handleToggle = useCallback(
    (enabled: boolean) => {
      onChange?.({ ...formData, quickFigures: { ...formData.quickFigures, enabled } });
      reportsWeeklyCardAnalytics.clicked([`Quick Figures Toggle ${enabled ? "On" : "Off"}`]);
    },
    [formData, onChange]
  );

  const isOptionDisabled = useCallback(
    (val: string) => {
      const config = formData.quickFigures.config;
      // disabled if selected MAX_OPTIONS, but enable if this option is one of selected
      return config && config.length >= MAX_METRICS_ALLOWED && !config.includes(val);
    },
    [formData]
  );

  return (
    <Section
      active={formData.quickFigures.enabled}
      description={intl.formatMessage(
        {
          id: "reports.weeklyReportCard.quickFigures.description",
          defaultMessage:
            "Select up to {value} {value, plural, one{figure} other{figures}} to be sent every week. The email will include the metric and its change from the previous week.",
          description: "Description for Quick Figures Section",
        },
        {
          value: MAX_METRICS_ALLOWED,
        }
      )}
      disabled={!formData.enabled}
      onToggleActivation={handleToggle}
      title={intl.formatMessage({
        id: "reports.weeklyReportCard.quickFigures",
        defaultMessage: "Quick Figures",
        description: "Title for Quick Figures Section",
      })}
      tooltip={
        formData.quickFigures.enabled
          ? intl.formatMessage({
              id: "reports.weeklyReportCard.disableSection",
              defaultMessage: "Toggle to hide this section in your email",
              description: "Title for toggle on weekly reports",
            })
          : intl.formatMessage({
              id: "reports.weeklyReportCard.enableSection",
              defaultMessage: "Toggle to show this section in your email",
              description: "Title for toggle on weekly reports",
            })
      }
    >
      <Checkbox.Group
        className={styles.checkBoxGroup}
        disabled={!formData.enabled || !formData.quickFigures.enabled}
        onChange={(config) =>
          onChange?.({
            ...formData,
            quickFigures: { ...formData.quickFigures, config: config as string[] },
          })
        }
        value={formData.quickFigures.config}
      >
        <Row>
          {weeklyReportsOptions.map((item, key) => (
            <Col key={key} lg={8} sm={12}>
              <Checkbox disabled={isOptionDisabled(item.value)} value={item.value}>
                {item.label}
              </Checkbox>
            </Col>
          ))}
        </Row>
      </Checkbox.Group>
    </Section>
  );
};

export default QuickFigures;
